import { get, isNil, isObject, toPath } from 'lodash';

import PAYLOAD_CONDITION_OPERATORS from './PAYLOAD_CONDITION_OPERATORS';
import decodeInputEvent from './decodeInputEvent';
import isActivePayloadCondition from './isActivePayloadCondition';

// Filters the already-fetched events client-side against the advanced-search conditions — no backend
// involvement. Conditions evaluate against each event's decoded input payload and AND together.
export default function filterEventsByPayload({ events, conditions }) {
  const activeConditions = conditions.filter(isActivePayloadCondition);

  if (activeConditions.length === 0) {
    return events;
  }

  return events.filter(event => {
    const payload = decodeInputEvent(event);
    return activeConditions.every(condition => matchesCondition({ payload, condition }));
  });
}

function matchesCondition({ payload, condition }) {
  const values = resolvePathValues(payload, condition.path);
  const conditionValue = condition.value.toLowerCase();

  switch (condition.operator) {
    case PAYLOAD_CONDITION_OPERATORS.EXISTS:
      return values.length > 0;
    case PAYLOAD_CONDITION_OPERATORS.NOT_EXISTS:
      return values.length === 0;
    case PAYLOAD_CONDITION_OPERATORS.EQUALS:
      return values.some(value => stringifyValue(value) === conditionValue);
    case PAYLOAD_CONDITION_OPERATORS.NOT_EQUALS:
      return !values.some(value => stringifyValue(value) === conditionValue);
    case PAYLOAD_CONDITION_OPERATORS.CONTAINS:
      return values.some(value => stringifyValue(value).includes(conditionValue));
    default:
      return true;
  }
}

// Resolves every non-null value at a dot-path. When a segment lands on an array
// without an explicit index (properties.products.sku), it fans out across the
// elements, so conditions match if ANY resolved value matches.
function resolvePathValues(payload, path) {
  // The field autocomplete suggests array paths with a `[*]` wildcard (properties.products[*].sku),
  // but the engine fans out across array elements on a plain segment — so strip the wildcard (and
  // trim stray whitespace) before resolving, so suggested and hand-typed paths both match.
  const normalizedPath = path.trim().replace(/\[\*\]/g, '');
  return toPath(normalizedPath)
    .reduce(collectSegmentValues, [payload])
    .filter(value => !isNil(value));
}

function collectSegmentValues(values, segment) {
  return values.flatMap(value => {
    if (Array.isArray(value) && !/^\d+$/.test(segment)) {
      return value.map(element => get(element, [segment]));
    }
    return [get(value, [segment])];
  });
}

// All comparisons are case-insensitive — when debugging events, "SPRING10" and
// "spring10" should both find the coupon.
function stringifyValue(value) {
  const stringValue = isObject(value) ? JSON.stringify(value) : String(value);
  return stringValue.toLowerCase();
}
